'use client';

import { classNames } from '@schuelerhilfe/shared/src/utils/classNames';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import type {
  NavigationGridCardResolved,
  NavigationGridResolved,
} from './types';
import { Collapsible } from '@/components/Collapsible';
import { Icon, IconType } from '@/components/Icon';
import { PlzFinder } from '@/components/PlzFinder';
import { useSubstitute } from '@/lib/render/RenderContextProvider';
import { RichText } from '@/lib/render/RichText';
import { substituteHref } from '@/lib/render/substituteHref';
import {
  getSectionColorClass,
  type SectionBackgroundColor,
} from '@/lib/section-colors';

function sortedForMobile(
  cards: NavigationGridCardResolved[],
): NavigationGridCardResolved[] {
  return [...cards].sort((a, b) => {
    if (a.mobileOrder != null && b.mobileOrder != null) {
      return a.mobileOrder - b.mobileOrder;
    }
    if (a.mobileOrder != null) {
      return -1;
    }
    if (b.mobileOrder != null) {
      return 1;
    }
    return 0;
  });
}

/**
 * The card's link, substituted so a template can point each URL at its own
 * sub-page (SH-497). Renders nothing when a placeholder stays unresolved.
 */
function useCardLink(
  url: string,
  label: string | null,
): { href: string; text: string } | null {
  const sub = useSubstitute();
  const href = substituteHref(url, sub);
  return href ? { href, text: (label && sub(label)) || href } : null;
}

function DesktopCardLink({
  url,
  label,
}: {
  url: string;
  label: string | null;
}) {
  const link = useCardLink(url, label);
  if (!link) {
    return null;
  }
  return (
    <a
      href={link.href}
      className="text-primary mt-4 mb-6 block text-center text-lg font-medium hover:underline"
    >
      {link.text}
    </a>
  );
}

function MobileCardLink({ url, label }: { url: string; label: string | null }) {
  const link = useCardLink(url, label);
  if (!link) {
    return null;
  }
  return (
    <a
      href={link.href}
      className="text-primary mt-3 inline-flex items-center font-medium"
    >
      {link.text}
    </a>
  );
}

function DesktopCard({
  card,
  background,
}: {
  card: NavigationGridCardResolved;
  background: SectionBackgroundColor;
}) {
  const t = useTranslations('navigationGrid');
  return (
    <div
      className={classNames(
        'flex flex-col rounded-2xl p-3',
        getSectionColorClass(background),
      )}
    >
      {/* Titles are CMS-authored and can be long German compounds, so they
          wrap and hyphenate rather than running out of the card. `hyphens-auto`
          uses the dictionary for the page's `lang` (set in app/layout.tsx);
          `break-words` is the fallback for a word with no legal break. */}
      <h3 className="mb-2 text-2xl font-medium break-words hyphens-auto">
        {card.title}
      </h3>
      <div className="text-foreground/80 [&_a]:text-primary [&_a]:underline [&_strong]:font-medium">
        <RichText nodes={card.text ?? undefined} />
      </div>
      {card.linkUrl && (
        <DesktopCardLink url={card.linkUrl} label={card.linkLabel} />
      )}
      {card.hasPlzFinder && (
        <Collapsible
          centered
          header={
            <span className="text-primary font-bold">
              {t('plzFinderTitle')}
            </span>
          }
          className="bg-transparent px-0"
          contentClassName="px-0 pb-0"
        >
          <PlzFinder />
        </Collapsible>
      )}
    </div>
  );
}

function MobileCard({
  card,
  background,
}: {
  card: NavigationGridCardResolved;
  background: SectionBackgroundColor;
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div
      className={classNames('rounded-2xl', getSectionColorClass(background))}
    >
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
        className="flex w-full items-center justify-between p-4 text-left"
      >
        <span className="text-sm font-medium break-words hyphens-auto">
          {card.title}
        </span>
        <Icon
          src={IconType.SvgChevronDown}
          size={20}
          className={`shrink-0 transition-transform ${expanded ? 'rotate-180' : ''}`}
        />
      </button>
      <div hidden={!expanded} className="px-4 pb-4">
        <div className="text-foreground/80 [&_a]:text-primary [&_a]:underline [&_strong]:font-medium">
          <RichText nodes={card.text ?? undefined} />
        </div>
        {card.linkUrl && (
          <MobileCardLink url={card.linkUrl} label={card.linkLabel} />
        )}
        {card.hasPlzFinder && <PlzFinder />}
      </div>
    </div>
  );
}

export const NavigationGridComponent = ({
  resolved,
}: {
  resolved: NavigationGridResolved;
}) => {
  const { background, mobileBackground, cards } = resolved;
  if (cards.length === 0) {
    return null;
  }

  const mobileCards = sortedForMobile(cards);

  return (
    <div className="pt-4">
      {/* Desktop: 3-column grid, CMS order */}
      <div className="hidden gap-6 md:grid md:grid-cols-3">
        {cards.map((card) => (
          <DesktopCard key={card.id} card={card} background={background} />
        ))}
      </div>

      {/* Mobile: single column, sorted by mobileOrder */}
      <div className="flex flex-col gap-4 md:hidden">
        {mobileCards.map((card) => (
          <MobileCard key={card.id} card={card} background={mobileBackground} />
        ))}
      </div>
    </div>
  );
};
